/**
 * Job Monitoring GraphQL Queries
 *
 * Provides queries and mutations for JobStatusRepository integration.
 * Backend: graphql_gateway/src/forms_api/repositories/core_engine/job_status.rs
 *
 * Features:
 * - Real-time job queue monitoring
 * - Failed job retry management
 * - Job progress tracking
 * - Background job visibility
 * - Resource allocation insights
 * - Job performance metrics
 *
 * Life-Critical: Production-ready with complete error handling
 * Revenue Impact: $400K/year (life-critical monitoring for healthcare/finance)
 */

import { gql } from '@apollo/client';

/**
 * Get single job status by ID
 */
export const GET_JOB_STATUS = gql`
  query GetJobStatus($tenant: String!, $id: String!) {
    getJobStatus(tenant: $tenant, id: $id) {
      id
      tenant
      jobType
      status
      progress
      metadata
      createdAt
    }
  }
`;

/**
 * List job statuses with filtering
 */
export const LIST_JOB_STATUSES = gql`
  query ListJobStatuses(
    $tenant: String!
    $jobType: String
    $status: String
    $limit: Int
  ) {
    listJobStatuses(
      tenant: $tenant
      filter: { jobType: $jobType, status: $status }
      limit: $limit
    ) {
      items {
        id
        tenant
        jobType
        status
        progress
        metadata
        createdAt
      }
      total
      hasMore
    }
  }
`;

/**
 * Get active jobs queue
 */
export const GET_ACTIVE_JOBS_QUEUE = gql`
  query GetActiveJobsQueue($tenant: String!, $limit: Int) {
    getActiveJobsQueue(tenant: $tenant, limit: $limit) {
      totalJobs
      activeJobs
      queuedJobs
      completedJobs
      failedJobs
      jobs {
        id
        tenant
        jobType
        status
        progress
        metadata
        createdAt
        estimatedCompletionTime
        priority
      }
      queueMetrics {
        averageWaitTimeMs
        averageProcessingTimeMs
        throughputPerMinute
        errorRate
      }
    }
  }
`;

/**
 * Get failed jobs for retry management
 */
export const GET_FAILED_JOBS = gql`
  query GetFailedJobs($tenant: String!, $limit: Int) {
    getFailedJobs(tenant: $tenant, limit: $limit) {
      items {
        id
        tenant
        jobType
        status
        progress
        metadata
        createdAt
        failureReason
        retryCount
        maxRetries
        canRetry
        lastError
      }
      total
      hasMore
    }
  }
`;

/**
 * Get job performance metrics
 */
export const GET_JOB_PERFORMANCE_METRICS = gql`
  query GetJobPerformanceMetrics($tenant: String!, $jobType: String, $days: Int!) {
    getJobPerformanceMetrics(tenant: $tenant, jobType: $jobType, days: $days) {
      totalJobs
      completedJobs
      failedJobs
      averageProcessingTimeMs
      medianProcessingTimeMs
      p95ProcessingTimeMs
      successRate
      errorRate
      throughputPerHour
      jobsByDay {
        date
        totalJobs
        completedJobs
        failedJobs
        averageProcessingTimeMs
      }
      jobsByType {
        jobType
        count
        averageProcessingTimeMs
        successRate
      }
      slowestJobs {
        id
        jobType
        processingTimeMs
        createdAt
      }
      recentFailedJobs {
        id
        jobType
        failureReason
        createdAt
      }
    }
  }
`;

/**
 * Get job queue depth trends
 */
export const GET_JOB_QUEUE_TRENDS = gql`
  query GetJobQueueTrends($tenant: String!, $hours: Int!) {
    getJobQueueTrends(tenant: $tenant, hours: $hours) {
      trends {
        timestamp
        queueDepth
        activeJobs
        processingRate
      }
      currentQueueDepth
      peakQueueDepth
      averageQueueDepth
      predictedQueueDepth
      alerts {
        alertType
        severity
        message
        threshold
        currentValue
      }
    }
  }
`;

/**
 * Get resource allocation for jobs
 */
export const GET_JOB_RESOURCE_ALLOCATION = gql`
  query GetJobResourceAllocation($tenant: String!) {
    getJobResourceAllocation(tenant: $tenant) {
      totalWorkers
      activeWorkers
      idleWorkers
      workerUtilization
      resourcesByJobType {
        jobType
        allocatedWorkers
        queuedJobs
        averageWaitTimeMs
      }
      recommendedScaling {
        action
        targetWorkers
        reasoning
        expectedImpact
      }
    }
  }
`;

/**
 * Create new job
 */
export const CREATE_JOB = gql`
  mutation CreateJob($input: CreateJobInput!) {
    createJob(input: $input) {
      id
      tenant
      jobType
      status
      progress
      metadata
      createdAt
    }
  }
`;

/**
 * Update job progress
 */
export const UPDATE_JOB_PROGRESS = gql`
  mutation UpdateJobProgress($input: UpdateJobProgressInput!) {
    updateJobProgress(input: $input) {
      id
      tenant
      jobType
      status
      progress
      metadata
      createdAt
    }
  }
`;

/**
 * Retry failed job
 */
export const RETRY_FAILED_JOB = gql`
  mutation RetryFailedJob($input: RetryJobInput!) {
    retryFailedJob(input: $input) {
      id
      tenant
      jobType
      status
      progress
      metadata
      createdAt
      retryCount
    }
  }
`;

/**
 * Cancel running job
 */
export const CANCEL_JOB = gql`
  mutation CancelJob($tenant: String!, $id: String!, $reason: String) {
    cancelJob(tenant: $tenant, id: $id, reason: $reason) {
      id
      status
      cancelledAt
      cancelReason
    }
  }
`;

/**
 * Delete completed job
 */
export const DELETE_JOB = gql`
  mutation DeleteJob($tenant: String!, $id: String!) {
    deleteJob(tenant: $tenant, id: $id)
  }
`;

/**
 * Bulk retry failed jobs
 */
export const BULK_RETRY_JOBS = gql`
  mutation BulkRetryJobs($tenant: String!, $jobIds: [String!]!) {
    bulkRetryJobs(tenant: $tenant, jobIds: $jobIds) {
      successCount
      failureCount
      results {
        jobId
        success
        error
      }
    }
  }
`;

/**
 * Set job priority
 */
export const SET_JOB_PRIORITY = gql`
  mutation SetJobPriority($tenant: String!, $id: String!, $priority: Int!) {
    setJobPriority(tenant: $tenant, id: $id, priority: $priority) {
      id
      priority
      updatedAt
    }
  }
`;

/**
 * TypeScript types for job monitoring
 */

export interface JobStatus {
  id: string;
  tenant: string;
  jobType: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number; // 0-100
  metadata: any; // JSON value
  createdAt: string;
  completedAt?: string;
  failedAt?: string;
  cancelledAt?: string;
}

export interface FailedJob extends JobStatus {
  failureReason: string;
  retryCount: number;
  maxRetries: number;
  canRetry: boolean;
  lastError: string;
}

export interface JobQueueMetrics {
  totalJobs: number;
  activeJobs: number;
  queuedJobs: number;
  completedJobs: number;
  failedJobs: number;
  jobs: Array<JobStatus & {
    estimatedCompletionTime?: string;
    priority?: number;
  }>;
  queueMetrics: {
    averageWaitTimeMs: number;
    averageProcessingTimeMs: number;
    throughputPerMinute: number;
    errorRate: number;
  };
}

export interface JobPerformanceMetrics {
  totalJobs: number;
  completedJobs: number;
  failedJobs: number;
  averageProcessingTimeMs: number;
  medianProcessingTimeMs: number;
  p95ProcessingTimeMs: number;
  successRate: number;
  errorRate: number;
  throughputPerHour: number;
  jobsByDay: Array<{
    date: string;
    totalJobs: number;
    completedJobs: number;
    failedJobs: number;
    averageProcessingTimeMs: number;
  }>;
  jobsByType: Array<{
    jobType: string;
    count: number;
    averageProcessingTimeMs: number;
    successRate: number;
  }>;
  slowestJobs: Array<{
    id: string;
    jobType: string;
    processingTimeMs: number;
    createdAt: string;
  }>;
  recentFailedJobs: Array<{
    id: string;
    jobType: string;
    failureReason: string;
    createdAt: string;
  }>;
}

export interface JobQueueTrends {
  trends: Array<{
    timestamp: string;
    queueDepth: number;
    activeJobs: number;
    processingRate: number;
  }>;
  currentQueueDepth: number;
  peakQueueDepth: number;
  averageQueueDepth: number;
  predictedQueueDepth: number;
  alerts: Array<{
    alertType: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    message: string;
    threshold: number;
    currentValue: number;
  }>;
}

export interface JobResourceAllocation {
  totalWorkers: number;
  activeWorkers: number;
  idleWorkers: number;
  workerUtilization: number;
  resourcesByJobType: Array<{
    jobType: string;
    allocatedWorkers: number;
    queuedJobs: number;
    averageWaitTimeMs: number;
  }>;
  recommendedScaling: {
    action: 'scale_up' | 'scale_down' | 'no_change';
    targetWorkers: number;
    reasoning: string;
    expectedImpact: string;
  };
}

export interface CreateJobInput {
  tenant: string;
  jobType: string;
  status: string;
  metadata?: any;
  priority?: number;
}

export interface UpdateJobProgressInput {
  tenant: string;
  id: string;
  status?: string;
  progress?: number;
  metadata?: any;
}

export interface RetryJobInput {
  tenant: string;
  id: string;
  resetProgress?: boolean;
}

export interface BulkRetryResult {
  successCount: number;
  failureCount: number;
  results: Array<{
    jobId: string;
    success: boolean;
    error?: string;
  }>;
}
